import { SORT_TYPE } from 'shared/filter/sort/types';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';

export type IssueGridFilterType =
  | 'direct'
  | 'oneOfDirect'
  | 'dateRange'
  | 'string';

export interface IssueGridFieldConfig {
  path: string;
  filterType?: IssueGridFilterType;
  sortType?: SORT_TYPE;
}

const primaryDataPath = (fieldName: string): string =>
  `primaryData.${fieldName}`;
const extendedDataPath = (fieldName: string): string =>
  `extendedData.${fieldName}`;

export const ISSUE_GRID_FIELD_CONFIG = {
  [IssueFieldNames.issueNumber]: {
    path: 'hashtag',
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.title]: {
    path: primaryDataPath(IssueFieldNames.title),
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.description]: {
    path: primaryDataPath(IssueFieldNames.description),
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  sites: {
    path: primaryDataPath(IssueFieldNames.site),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.site]: {
    path: primaryDataPath(IssueFieldNames.site),
    filterType: 'direct',
  },
  levels: {
    path: primaryDataPath(IssueFieldNames.level),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.level]: {
    path: primaryDataPath(IssueFieldNames.level),
    filterType: 'direct',
  },
  [IssueFieldNames.assignee]: {
    path: primaryDataPath(IssueFieldNames.assignee),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.executor]: {
    path: primaryDataPath(IssueFieldNames.executor),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  process: { path: 'process', filterType: 'direct', sortType: SORT_TYPE.STRING },
  workflow: {
    path: 'workflowId',
    filterType: 'direct',
  },
  [IssueFieldNames.stage]: {
    path: 'stageId',
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  lastDecision: {
    path: 'lastDecisionStatus',
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  createdAt: { path: 'createdAt', filterType: 'dateRange', sortType: SORT_TYPE.NUMBERS },
  modifiedAt: {
    path: 'modifiedAt',
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.createdBy]: {
    path: IssueFieldNames.createdBy,
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.modifiedBy]: {
    path: IssueFieldNames.modifiedBy,
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.detectionDate]: {
    path: primaryDataPath(IssueFieldNames.detectionDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.targetCompletionDate]: {
    path: extendedDataPath(IssueFieldNames.targetCompletionDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.finalCompletionDate]: {
    path: extendedDataPath(IssueFieldNames.finalCompletionDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.targetStartDate]: {
    path: extendedDataPath(IssueFieldNames.targetStartDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.finalStartDate]: {
    path: extendedDataPath(IssueFieldNames.finalStartDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.targetResponseToAuthorDate]: {
    path: extendedDataPath(IssueFieldNames.targetResponseToAuthorDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.finalResponseToAuthorDate]: {
    path: extendedDataPath(IssueFieldNames.finalResponseToAuthorDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.proposedCorrectiveAction]: {
    path: extendedDataPath(IssueFieldNames.proposedCorrectiveAction),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.environmentalAspect]: {
    path: extendedDataPath(IssueFieldNames.environmentalAspect),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.impact]: {
    path: extendedDataPath(IssueFieldNames.impact),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.decisionToImposeFine]: {
    path: extendedDataPath(IssueFieldNames.decisionToImposeFine),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.effect]: {
    path: extendedDataPath(IssueFieldNames.effect),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.circumstances]: {
    path: extendedDataPath(IssueFieldNames.circumstances),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.spilledSubstance]: {
    path: extendedDataPath(IssueFieldNames.spilledSubstance),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.issueVerification]: {
    path: extendedDataPath(IssueFieldNames.issueVerification),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.delayReason]: {
    path: extendedDataPath(IssueFieldNames.delayReason),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.rejectionReason]: {
    path: extendedDataPath(IssueFieldNames.rejectionReason),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.subcontractors]: {
    path: primaryDataPath(IssueFieldNames.subcontractors),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.contractNumbers]: {
    path: primaryDataPath(IssueFieldNames.contractNumbers),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.workTypes]: {
    path: extendedDataPath(IssueFieldNames.workTypes),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.rootCauses]: {
    path: extendedDataPath(IssueFieldNames.rootCauses),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.hazardCategory]: {
    path: extendedDataPath(IssueFieldNames.hazardCategory),
    filterType: 'oneOfDirect',
  },
  inspection: { path: 'inspection', filterType: 'direct' },
  inspectionNumber: {
    path: 'inspectionNumberSearch',
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.executedByCompanies]: {
    path: extendedDataPath(IssueFieldNames.executedByCompanies),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.subcontractorRepresentative]: {
    path: extendedDataPath(IssueFieldNames.subcontractorRepresentative),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.estimatedCost]: {
    path: extendedDataPath(IssueFieldNames.estimatedCost),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.finalCost]: {
    path: extendedDataPath(IssueFieldNames.finalCost),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.expectedFine]: {
    path: extendedDataPath(IssueFieldNames.expectedFine),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.imposedFine]: {
    path: extendedDataPath(IssueFieldNames.imposedFine),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.targetAmount]: {
    path: extendedDataPath(IssueFieldNames.targetAmount),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.completedAmount]: {
    path: extendedDataPath(IssueFieldNames.completedAmount),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.amountPercentage]: {
    path: extendedDataPath(IssueFieldNames.amountPercentage),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.waterLeakageScale]: {
    path: extendedDataPath(IssueFieldNames.waterLeakageScale),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.soilLeakageScale]: {
    path: extendedDataPath(IssueFieldNames.soilLeakageScale),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.contaminatedSoilScale]: {
    path: extendedDataPath(IssueFieldNames.contaminatedSoilScale),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.numberOfEmployees]: {
    path: extendedDataPath(IssueFieldNames.numberOfEmployees),
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.daysOfInabilityToWork]: {
    path: extendedDataPath(IssueFieldNames.daysOfInabilityToWork),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.costCode]: {
    path: extendedDataPath(IssueFieldNames.costCode),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.personUnableToWork]: {
    path: extendedDataPath(IssueFieldNames.personUnableToWork),
    filterType: 'string',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.startDateDelay]: {
    path: extendedDataPath(IssueFieldNames.startDateDelay),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.completionDateDelay]: {
    path: extendedDataPath(IssueFieldNames.completionDateDelay),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.responseToAuthorDelay]: {
    path: extendedDataPath(IssueFieldNames.responseToAuthorDelay),
    filterType: 'string',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.solutionProposal]: {
    path: extendedDataPath(IssueFieldNames.solutionProposal),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.solutionMethod]: {
    path: extendedDataPath(IssueFieldNames.solutionMethod),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.priority]: {
    path: extendedDataPath(IssueFieldNames.priority),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.projectStage]: {
    path: extendedDataPath(IssueFieldNames.projectStage),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.sickLeaveStartDate]: {
    path: extendedDataPath(IssueFieldNames.sickLeaveStartDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.sickLeaveEndDate]: {
    path: extendedDataPath(IssueFieldNames.sickLeaveEndDate),
    filterType: 'dateRange',
    sortType: SORT_TYPE.NUMBERS,
  },
  [IssueFieldNames.supervisor]: {
    path: extendedDataPath(IssueFieldNames.supervisor),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.victim]: {
    path: extendedDataPath(IssueFieldNames.victim),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.victimJobTitle]: {
    path: extendedDataPath(IssueFieldNames.victimJobTitle),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.employeeNumber]: {
    path: extendedDataPath(IssueFieldNames.employeeNumber),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.employmentType]: {
    path: extendedDataPath(IssueFieldNames.employmentType),
    filterType: 'direct',
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.immediateMeasures]: {
    path: extendedDataPath(IssueFieldNames.immediateMeasures),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.victimActivity]: {
    path: extendedDataPath(IssueFieldNames.victimActivity),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.injuryTypes]: {
    path: extendedDataPath(IssueFieldNames.injuryTypes),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.injuredBodyParts]: {
    path: extendedDataPath(IssueFieldNames.injuredBodyParts),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.applicationNumber]: {
    path: extendedDataPath(IssueFieldNames.applicationNumber),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.productType]: {
    path: extendedDataPath(IssueFieldNames.productType),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.producerDistributor]: {
    path: extendedDataPath(IssueFieldNames.producerDistributor),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.typeModel]: {
    path: extendedDataPath(IssueFieldNames.typeModel),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.installationLocation]: {
    path: extendedDataPath(IssueFieldNames.installationLocation),
    sortType: SORT_TYPE.STRING,
  },
  [IssueFieldNames.requirements]: {
    path: extendedDataPath(IssueFieldNames.requirements),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.installationBasis]: {
    path: extendedDataPath(IssueFieldNames.installationBasis),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.constructionProductDocuments]: {
    path: extendedDataPath(IssueFieldNames.constructionProductDocuments),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.deviceDocuments]: {
    path: extendedDataPath(IssueFieldNames.deviceDocuments),
    filterType: 'oneOfDirect',
  },
  [IssueFieldNames.additionalDocuments]: {
    path: extendedDataPath(IssueFieldNames.additionalDocuments),
    filterType: 'oneOfDirect',
  },
} satisfies Record<string, IssueGridFieldConfig>;

type RequiredIssueGridFieldMappings = Record<
  IssueFieldNames.priority | IssueFieldNames.projectStage,
  IssueGridFieldConfig
>;

const requiredIssueGridFieldMappings: RequiredIssueGridFieldMappings =
  ISSUE_GRID_FIELD_CONFIG;
void requiredIssueGridFieldMappings;

const issueGridFieldConfigEntries = Object.entries(
  ISSUE_GRID_FIELD_CONFIG
) as [string, IssueGridFieldConfig][];

export const SORTABLE_ISSUE_FIELDS = new Set(
  issueGridFieldConfigEntries
    .filter(([, config]) => config.sortType != null)
    .map(([field]) => field)
);

export const FIELD_TO_SORT_CONFIG = Object.fromEntries(
  issueGridFieldConfigEntries
    .filter(([, config]) => config.sortType != null)
    .map(([field, config]) => [field, { path: config.path, type: config.sortType! }])
) as Record<string, { path: string; type: SORT_TYPE }>;

export const FIELD_TO_FILTER_CONFIG = Object.fromEntries(
  issueGridFieldConfigEntries
    .filter(([, c]) => c.filterType != null)
    .map(([field, c]) => [field, { path: c.path, type: c.filterType! }])
) as Record<string, { path: string; type: IssueGridFilterType }>;

const ISSUE_HIDDEN_DIRECT_FILTER_FIELDS: IssueFieldNames[] = [
  IssueFieldNames.priority,
  IssueFieldNames.projectStage,
  IssueFieldNames.impact,
  IssueFieldNames.environmentalAspect,
  IssueFieldNames.effect,
  IssueFieldNames.decisionToImposeFine,
  IssueFieldNames.circumstances,
  IssueFieldNames.issueVerification,
  IssueFieldNames.delayReason,
  IssueFieldNames.rejectionReason,
  IssueFieldNames.proposedCorrectiveAction,
  IssueFieldNames.spilledSubstance,
  IssueFieldNames.supervisor,
];

export const ISSUE_HIDDEN_DIRECT_FILTER_PATHS = new Set(
  ISSUE_HIDDEN_DIRECT_FILTER_FIELDS
    .map((fieldName) => FIELD_TO_FILTER_CONFIG[fieldName]?.path)
    .filter((path): path is string => typeof path === 'string')
);
